import * as React from 'react';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import { ChevronDown, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn(className)} {...props} />);
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    disabledMessage?: string;
    showChevron?: boolean;
    showDisabledMessage?: boolean;
    disableWrapper?: boolean;
    headerClassName?: string;
  }
>(
  (
    {
      className,
      children,
      disabled,
      disabledMessage,
      showDisabledMessage = true,
      showChevron = true,
      disableWrapper = false,
      headerClassName,
      ...props
    },
    ref,
  ) => {
    const tCommon = useTranslations('common');

    const triggerContent = (
      <AccordionPrimitive.Trigger
        ref={ref}
        disabled={disabled}
        className={cn(
          'flex h-12 focus-within:outline-none focus-visible:bg-black/4 dark:focus-visible:bg-white/4 flex-1 items-center justify-between border-y border-b-ui-void-gap border-t-toolbar-bevel bg-ui-base p-3 py-2 text-sm font-bold text-black transition-all hover:no-underline disabled:text-text-gray-alternate @[18rem]/inspector:py-3 dark:text-white dark:disabled:text-white/50 [&[data-state=open]>svg]:rotate-180',
          className,
        )}
        {...props}
      >
        {children}

        {disabled && showDisabledMessage ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Info className="icon-5 shrink-0 text-icon-base transition-transform duration-200" strokeWidth={2} />
              </div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent className="text-balance">
                {disabledMessage ?? tCommon('multiSelectLimitedOptions')}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        ) : (
          showChevron && (
            <ChevronDown
              className="-mr-px icon-5.5 shrink-0 text-icon-base transition-transform duration-200"
              strokeWidth={2}
            />
          )
        )}
      </AccordionPrimitive.Trigger>
    );

    if (disableWrapper) {
      return triggerContent;
    }

    return (
      <AccordionPrimitive.Header className={cn('flex', headerClassName)}>{triggerContent}</AccordionPrimitive.Header>
    );
  },
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'flex flex-col p-3 overflow-hidden border-b border-b-ui-void-gap bg-ui-panel text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      className,
    )}
    {...props}
  >
    {children}
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
